import { useState } from "react";
import styled from "styled-components";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT,
} from "helpers/designConstants";
import { MiniButton } from "components/standardElements";
import { SIZE_ORDER } from "services/supabaseSearch";
import InventoryDialog from "./InventoryDialog";
import { toWarehouseCodes } from "./warehouseFilters";
import {
  computeIssueDrift,
  describeChange,
  formatContactedAt,
} from "./contactDrift";

// Per-pill clear-date formatter. `uncovered` = no future incoming will
// clear this size (scanner stored null). `overdue` = the date is in the
// past (shipment was expected but inventory still reads negative — rep
// should expect a delay). `upcoming` = normal future date.
function formatClearDate(dateStr) {
  if (!dateStr) return { label: "no incoming", status: "uncovered" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  const deltaDays = Math.round((d - today) / 86400000);
  const mmm = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const year = d.getFullYear();
  const thisYear = today.getFullYear();
  const label = year === thisYear ? `${mmm} ${day}` : `${mmm} ${day}, ${year}`;
  return { label, status: deltaDays < 0 ? "overdue" : "upcoming" };
}

export default function StockIssueCompactRow({
  issue,
  product,
  projectFirebaseId,
  projectWarehouses,
  onUpdateStatus,
}) {
  const [showInventory, setShowInventory] = useState(false);
  const [busy, setBusy] = useState(false);

  const clearDates = issue.oos_size_clear_dates || {};
  const destinations = issue.oos_size_destinations || {};
  const qcPending = issue.qc_pending_since || {};
  const image = product?.imageURL;
  const itemNumber = product?.itemNumber || issue.product_id;
  const name = product?.name || "";
  const color = product?.color || "";
  const drift = computeIssueDrift(issue);
  const contactedLabel = formatContactedAt(issue.contact_snapshot?.at);

  const handle = async (status) => {
    setBusy(true);
    try {
      await onUpdateStatus(issue.id, status);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Row>
        <ImageWrapper>
          {image ? (
            <img
              src={image}
              alt={name}
              onError={(e) => {
                e.target.onError = null;
                e.currentTarget.src = "/st_placeholder.svg";
              }}
            />
          ) : (
            <img src="/st_placeholder.svg" alt={name} />
          )}
        </ImageWrapper>

        <InfoWrapper>
          <TitleLine>
            <ItemNumber>{itemNumber}</ItemNumber>
            {name && <ProductName>{name}</ProductName>}
            {color && <ProductColor>({color})</ProductColor>}
          </TitleLine>
          {issue.oos_sizes && issue.oos_sizes.length > 0 && (
            <OosRow>
              {[...issue.oos_sizes]
                .sort(
                  (a, b) =>
                    (SIZE_ORDER[a] ?? 99) - (SIZE_ORDER[b] ?? 99),
                )
                .map((s) => {
                  const clear = formatClearDate(clearDates[s]);
                  // toWarehouseCodes drops legacy codes (CEW) from the
                  // destinations list before it's rendered or compared.
                  const destList = toWarehouseCodes(destinations[s] || []);
                  // Hide the destination suffix when it covers every
                  // warehouse this project ships to — in that case it's
                  // always the full set and adds no signal. Keep it when
                  // destinations are a proper subset (e.g. a multi-region
                  // project where only USW is failing).
                  const coversAll =
                    projectWarehouses &&
                    projectWarehouses.length > 0 &&
                    projectWarehouses.every((w) => destList.includes(w));
                  const showDestinations = destList.length > 0 && !coversAll;
                  const isQcPending = Boolean(qcPending[s]);
                  return (
                    <OosSizePill key={s} status={clear.status}>
                      <SizeLabel>{s}</SizeLabel>
                      <DateLabel>{clear.label}</DateLabel>
                      {isQcPending && (
                        <QcLabel>(in receiving)</QcLabel>
                      )}
                      {showDestinations && (
                        <DestLabel>({destList.join(", ")})</DestLabel>
                      )}
                    </OosSizePill>
                  );
                })}
            </OosRow>
          )}
          {drift.drifted && (
            <DriftBlock>
              <DriftHeader>
                {issue.status === "dismissed" ? "Since you dismissed" : "Since you contacted"}
                {contactedLabel ? ` ${contactedLabel}` : ""}
              </DriftHeader>
              <DriftList>
                {drift.changes
                  .map(describeChange)
                  .filter(Boolean)
                  .map((text) => (
                    <DriftItem key={text}>{text}</DriftItem>
                  ))}
              </DriftList>
            </DriftBlock>
          )}
        </InfoWrapper>

        <ActionGroup>
          <MiniButton
            type="button"
            onClick={() => setShowInventory(true)}
          >
            View inventory
          </MiniButton>
          <MiniButton
            type="button"
            destructive
            onClick={() => handle("dismissed")}
            disabled={busy}
          >
            {issue.status === "dismissed" ? "Keep dismissed" : "Dismiss"}
          </MiniButton>
        </ActionGroup>
      </Row>

      <InventoryDialog
        show={showInventory}
        onClose={() => setShowInventory(false)}
        product={product}
        projectFirebaseId={projectFirebaseId}
        oosSizes={issue.oos_sizes}
      />
    </>
  );
}

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid ${COLORS.secondary};

  &:last-child {
    border-bottom: none;
  }
`;

const ImageWrapper = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const InfoWrapper = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
  color: ${COLORS.text};
`;

const ItemNumber = styled.span`
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
`;

const ProductName = styled.span`
  color: ${COLORS.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const ProductColor = styled.span`
  color: ${COLORS.stormtechGrey};
`;

const OosRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

// Color encodes per-size severity now that the row-level CRITICAL/WARNING
// text pill is gone. `uncovered` (no date to tell the customer) is red —
// truly critical. Dated sizes are orange — warning-level, rep has an ETA.
// Overdue (past-dated) stays orange with stronger fill so a late shipment
// reads as "still a warning, just slipping."
const OosSizePill = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${FONTSIZE.xsmall};
  color: ${({ status }) =>
    status === "uncovered" ? COLORS.alertRed : COLORS.alertOrange};
  background: ${({ status }) => {
    if (status === "uncovered") return "rgba(204, 0, 0, 0.22)";
    if (status === "overdue") return "rgba(247, 147, 30, 0.28)";
    return "rgba(247, 147, 30, 0.12)";
  }};
  border: 1px solid
    ${({ status }) => {
      if (status === "uncovered") return COLORS.alertRed;
      if (status === "overdue") return COLORS.alertOrange;
      return "rgba(247, 147, 30, 0.45)";
    }};
`;

const SizeLabel = styled.span`
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
`;

const DateLabel = styled.span`
  font-size: 10px;
  font-weight: ${FONTWEIGHT.medium};
  opacity: 0.85;
`;

// Destination list reads as context, not a primary data point — slightly
// smaller + more muted than the date. Sits inside the pill so a rep can
// scan "what size / when / where" in one chunk.
const DestLabel = styled.span`
  font-size: 10px;
  font-weight: ${FONTWEIGHT.medium};
  opacity: 0.65;
`;

// QC-pending marker: shipment landed at dock but hasn't cleared receiving.
// Same muted treatment as destinations — we're adding context, not alarm.
const QcLabel = styled.span`
  font-size: 10px;
  font-weight: ${FONTWEIGHT.medium};
  opacity: 0.65;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

// Drift surface. Only renders when the row is contacted AND its
// contact_snapshot differs from live state. Plain, low-contrast treatment —
// the information, not the decoration, carries the signal.
const DriftBlock = styled.div`
  margin-top: 2px;
  padding: 4px 8px;
  border-left: 2px solid ${COLORS.stormtechGrey};
  background: ${COLORS.tertiary};
`;

const DriftHeader = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${COLORS.stormtechGrey};
  font-weight: ${FONTWEIGHT.heavy};
  margin-bottom: 2px;
`;

const DriftList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
`;

const DriftItem = styled.span`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.text};
`;

